// External libraries
import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'

// MUI components
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

// Redux
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../../store/store'
import { TimesheetStoreState } from '../../../../store/timesheetStore'

// Store slices & actions
import { handleDetails } from '../../../../store/slices/timesheet/timesheetPageSlice'
import { KmsVehicle } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'

// API hooks
import { useLazyCalculateAvailabilityQuery } from '../../../../store/api/timesheet/availabilityApi'

// Utils
import { filterWorkTimes } from '../../../../utils/timesheet/workTimes'
import { mapServicesForApproval, mapServicesForEdit } from '../../../../utils/timesheet/services'

// Modal
import { useModal } from '../../../../store/ModalProvider'
import { AvailabilityModalData, modalHandlersType } from '../../../../types/modal'

// Components
import CustomSelect from '../../../v2/general/CustomSelect/CustomSelect'
import CustomButton from '../../../v2/general/CustomButton/CustomButton'
import CustomTextField from '../../../general/CustomTextField/CustomTextField'
import CustomAlertDialog from '../../../general/CustomAlertDialog/CustomAlertDialog'

// Types and Interfaces
type WorkTypeOption = { id: number; value: string }

interface WorkRegimeAndAgentProps {
	workTypes?: WorkTypeOption[]
	isTimeSheetApproved: boolean
	workType: string
	isModal?: boolean
	handleWork: (event: SelectChangeEvent<string | string[]>) => void
}

/**
 * WorkRegimeAndAgent component
 * @description This component displays the work regime and agent information for a timesheet. It allows users to select a work type from a dropdown, view their unique agent time, and check their availability based on the services and work times associated with the timesheet. The component also provides an option to automatically recalculate availability when saving the timesheet. It can be rendered in a modal or as part of the main timesheet details page, with styling adjustments accordingly.
 * @param {WorkRegimeAndAgentProps} props - The props for the component, including work types, timesheet approval status, selected work type, modal state, and a handler for work type changes.
 * @returns {JSX.Element} The rendered WorkRegimeAndAgent component.
 */ function WorkRegimeAndAgent({
	workTypes,
	isTimeSheetApproved,
	workType,
	isModal = false,
	handleWork,
}: Readonly<WorkRegimeAndAgentProps>): React.JSX.Element {
	const TimesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const approvalAcceptState = useSelector((state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept)
	const dispatch = useDispatch<AppDispatch>()
	const { openModal, closeModal } = useModal()
	const [isInvalidDatesDialogOpen, setIsInvalidDatesDialogOpen] = useState(false)

	/**
	 * This effect runs whenever the filters in the timesheet details change and checks if the current filters match the default filters for the current tab. If they do, it triggers a refetch of the filtered timesheet data to ensure that the displayed data is updated according to the default filters for that tab.
	 * The dependencies for this effect include the filters from the timesheet details and the current tab from timesheetTabs to ensure it runs whenever the filters are updated or the user switches tabs.
	 * @see {@link useGetTimesheetsFilteredQuery} for the API query that fetches filtered timesheet data based on the current filters, which are updated when switching tabs.
	 */
	const [
		triggerCalculateAvailability,
		{ data: availability, isFetching: isFetchingAvailability, isError: isErrorAvailability },
	] = useLazyCalculateAvailabilityQuery()

	/**
	 * This effect runs whenever the component mounts and opens the availability modal if there is availability data available and it's not currently fetching or in an error state. It also defines handlers for saving changes and updating the driving break value, which are passed to the modal when opened. The dependencies for this effect include the availability data, fetching and error states, and the handlers to ensure it runs whenever any of these values change.
	 * @see {@link useLazyCalculateAvailabilityQuery} for the API query that fetches availability data, which is used to determine when to open the modal and what data to display in it.
	 * @see {@link openModal} for the function that opens the modal with the availability data and handlers.
	 */
	const onSaveChanges = useCallback(() => {
		closeModal()
	}, [])

	/**
	 * This handler updates the driving break value in the timesheet details when the user changes it in the availability modal. It dispatches an action to update the timesheet details in the Redux store with the new driving break value, ensuring that any changes made in the modal are reflected in the main timesheet details state. The dependencies for this handler include the dispatch function and the current timesheet details to ensure it has access to the latest state when updating the driving break value.
	 * @param {number} value - The new driving break value selected by the user in the availability modal.
	 * @see {@link handleDetails} for the Redux action that updates the timesheet details in the store with the new driving break value.
	 * @see {@link closeModal} for the function that closes the modal after saving changes.
	 * @see {@link useSelector} for accessing the current timesheet details from the Redux store to ensure the handler has the latest state when updating the driving break value.
	 */
	const onDrivingBreakChange = useCallback(
		(value: number) => {
			dispatch(
				handleDetails({
					dispDrivingBreak: value,
				}),
			)
		},
		[dispatch, TimesheetPage],
	)

	/**
	 * This function opens the availability modal with the relevant data when the availability data is available and not currently being fetched or in an error state. It prepares the data to be displayed in the modal based on the availability response from the API and dispatches an action to update the timesheet details with a unique agent time if applicable. The modal is opened with handlers for saving changes and updating the driving break value, allowing users to interact with their availability information and have any changes reflected in the main timesheet details state. The dependencies for this function include the availability data, fetching and error states, and the handlers to ensure it runs whenever any of these values change.
	 * @see {@link useLazyCalculateAvailabilityQuery} for the API query that fetches availability data, which is used to determine when to open the modal and what data to display in it.
	 * @see {@link handleDetails} for the Redux action that updates the timesheet details in the store with a unique agent time if applicable.
	 */
	const openAvailabilityModal = useCallback(() => {
		if (availability && !isFetchingAvailability && !isErrorAvailability) {
			const availabilityModalData: AvailabilityModalData = {
				workedSchedule: availability.workedSchedule,
				totalsByPeriod: availability.totalsByPeriod,
				totalAvailability: availability.totalAvailability,
				totalAvailabilityFormatted: availability.totalAvailabilityFormatted,
				overtime: availability.overtime,
				drivingBreak: availability.drivingBreak,
			}

			if (availability.singleAgent?.singleAgentTime) {
				dispatch(
					handleDetails({
						uniqueAgent: dayjs(`1900-01-01T${availability.singleAgent.singleAgentTime}`).format('YYYY-MM-DDTHH:mm:ss'),
					}),
				)
			}

			openModal('Disponibilidade e Pausas', availabilityModalData, [
				onSaveChanges,
				onDrivingBreakChange,
			] as modalHandlersType)
		}
	}, [availability, isFetchingAvailability, isErrorAvailability])

	/**
	 * This memoized value computes the work times and services to be used for availability calculations based on whether the timesheet is approved or not. It filters the work times and maps the services accordingly, ensuring that the correct data is used for availability calculations in both approved and non-approved states. The dependencies for this memo include the timesheet approval status, work times, and services from both the approval state and the main timesheet details to ensure it recomputes whenever any of these values change.
	 * @see {@link filterWorkTimes} for the utility function that filters work times based on specific criteria.
	 * @see {@link mapServicesForApproval} and {@link mapServicesForEdit} for the utility functions that map services for approval and editing states, respectively.
	 * @see {@link useSelector} for accessing the work times and services from both the approval state and the main timesheet details in the Redux store to compute the correct data for availability calculations.
	 */
	const { workTimes, services } = useMemo(() => {
		const sourceWorkTimes = isTimeSheetApproved ? approvalAcceptState.workTimes : TimesheetPage.workTimes
		const computedWorkTimes = filterWorkTimes(sourceWorkTimes)
		const computedServices = isTimeSheetApproved
			? mapServicesForEdit(approvalAcceptState.services)
			: mapServicesForApproval(TimesheetPage.services)
		return { workTimes: computedWorkTimes, services: computedServices }
	}, [
		isTimeSheetApproved,
		approvalAcceptState.workTimes,
		approvalAcceptState.services,
		TimesheetPage.workTimes,
		TimesheetPage.services,
	])

	/**
	 * This handler triggers the calculation of availability when the user clicks the "Ver Disponibilidade" button. It prepares the request body with the necessary data from the timesheet details, including services and work times, and calls the trigger function from the useLazyCalculateAvailabilityQuery hook to fetch the availability data from the API. The dependencies for this handler include the trigger function, timesheet ID, services, and work times to ensure it has access to the latest data when calculating availability.
	 * @see {@link useLazyCalculateAvailabilityQuery} for the API query that calculates availability based on the provided request body.
	 * @see {@link filterWorkTimes} for the utility function that filters work times based on specific criteria, which is used to prepare the work times for the availability calculation.
	 */
	const handleCalculateAvailability = useCallback(() => {
		const hasInvalidDates = services.some(service => {
			if (service.startDateTime && service.endDateTime) {
				return dayjs(service.endDateTime).isBefore(dayjs(service.startDateTime))
			}
			return false
		})

		if (hasInvalidDates) {
			setIsInvalidDatesDialogOpen(true)
			return
		}

		triggerCalculateAvailability({
			TimesheetId: TimesheetPage.timeSheetId,
			ServiceExecutionAvailabilityRequests: services.map(service => ({
				serviceId: service.serviceId,
				pax: service.pax,
				isClean: service.isClean,
				startDateTime: service.startDateTime,
				endDateTime: service.endDateTime,
				isAU: service?.isAU ?? false,
				kmsEmpty: (service.kmsVehicles ?? [])
					.filter((kv: KmsVehicle) => kv.inEmpty)
					.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
				kmsFull: (service.kmsVehicles ?? [])
					.filter((kv: KmsVehicle) => !kv.inEmpty)
					.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
				service: null,
			})),
			TimeSheetWorkTimeAvailabilityRequests: workTimes,
			PauseTypeId: 2,
		})
	}, [triggerCalculateAvailability, TimesheetPage.timeSheetId, services, workTimes])

	/**
	 * This handler updates the recalculation on save preference in the timesheet details when the user toggles the corresponding checkbox. It dispatches an action to update the timesheet details in the Redux store with the new recalculation preference, allowing users to choose whether they want their availability to be automatically recalculated when they save their timesheet. The dependencies for this handler include the dispatch function and the current timesheet details to ensure it has access to the latest state when updating the recalculation preference.
	 * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the checkbox input that indicates whether the user wants to enable or disable automatic recalculation on save.
	 * @see {@link handleDetails} for the Redux action that updates the timesheet details in the store with the new recalculation preference.
	 * @see {@link onSaveChanges} for the function that handles saving changes and closing the modal, which is relevant to the recalculation preference as it determines whether availability should be recalculated when saving.
	 * @see {@link useSelector} for accessing the current timesheet details from the Redux store to ensure the handler has the latest state when updating the recalculation preference.
	 */
	const handleRecalculateOnSave = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			dispatch(
				handleDetails({
					dispRecalculateOnSave: event.target.checked,
				}),
			)
		},
		[TimesheetPage, dispatch],
	)

	/**
	 * This memoized value computes the unique agent field to be displayed in the component based on the unique agent time from the timesheet details. It formats the unique agent time for display and returns a disabled text field with the formatted time. The dependencies for this memo include the unique agent time from the timesheet details to ensure it recomputes whenever this value changes.
	 * @see {@link useSelector} for accessing the unique agent time from the timesheet details in the Redux store to compute the value for the unique agent field.
	 */
	const handleUniqueAgentChange = useCallback(
		(newValue: dayjs.Dayjs | null) => {
			dispatch(
				handleDetails({
					uniqueAgent: newValue?.isValid() ? newValue.format('YYYY-MM-DDTHH:mm:ss') : null,
				}),
			)
		},
		[TimesheetPage, dispatch],
	)

	/**
	 * This memoized value computes the contract code field to be displayed in the component based on the selected work type and the available work types. It finds the corresponding work type object for the selected work type and returns a disabled text field with the contract code. The dependencies for this memo include the selected work type and the available work types to ensure it recomputes whenever either of these values changes.
	 * @see {@link useSelector} for accessing the selected work type and available work types from the Redux store to compute the value for the contract code field.
	 */
	const uniqueAgentField = useMemo(() => {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<CustomTextField
					disabled
					type='time'
					label='Agente Único'
					size='small'
					value={
						TimesheetPage.uniqueAgent
							? dayjs(TimesheetPage.uniqueAgent).format('HH:mm')
							: dayjs('1900-01-01T00:00:00Z').format('HH:mm')
					}
				/>
			</LocalizationProvider>
		)
	}, [TimesheetPage.uniqueAgent, handleUniqueAgentChange])

	/**
	 * This memoized value computes the contract code field to be displayed in the component based on the selected work type and the available work types. It finds the corresponding work type object for the selected work type and returns a disabled text field with the contract code. The dependencies for this memo include the selected work type and the available work types to ensure it recomputes whenever either of these values changes.
	 * @see {@link useSelector} for accessing the selected work type and available work types from the Redux store to compute the value for the contract code field.
	 */
	const contractCode = useMemo(() => {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<CustomTextField
					disabled
					type='number'
					label='Código Contrato'
					size='small'
					value={workTypes?.find(wt => String(wt.id) === workType)?.id ?? 0}
				/>
			</LocalizationProvider>
		)
	}, [])

	/**
	 * This effect runs whenever the component mounts and opens the availability modal if there is availability data available and it's not currently fetching or in an error state. It also defines handlers for saving changes and updating the driving break value, which are passed to the modal when opened. The dependencies for this effect include the availability data, fetching and error states, and the handlers to ensure it runs whenever any of these values change.
	 * @see {@link useLazyCalculateAvailabilityQuery} for the API query that fetches availability data, which is used to determine when to open the modal and what data to display in it.
	 * @see {@link openModal} for the function that opens the modal with the availability data and handlers.
	 */
	useEffect(() => {
		openAvailabilityModal()
	}, [openAvailabilityModal])

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					paddingTop: isModal ? 0 : '1.5rem',
				}}
			>
				<Typography
					variant='h5'
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 600,
						fontSize: isModal ? '1.5rem' : '1rem',
						lineHeight: '175%',
						letterSpacing: '0.15px',
						color: 'var(--primary-dark)',
					}}
				>
					Regime de Trabalho e {isModal ? 'Disponibilidade' : 'Agente Único'}
				</Typography>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'baseline',
						justifyContent: 'space-between',
						paddingTop: '1.5rem',
						gap: '2rem',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between' }}>
						<CustomSelect
							defaultValue={workType}
							options={workTypes?.map(type => ({
								value: String(type.id),
								label: type.value,
							}))}
							tabIndex={-1}
							value={workType}
							label='Regime de Trabalho'
							onChange={e => handleWork(e as SelectChangeEvent<string | string[]>)}
							size='small'
							sx={{
								width: '300px',
							}}
						/>
						{!isModal && uniqueAgentField}
						{isModal && contractCode}
					</Box>

					{!isModal && (
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
								<Typography
									variant='subtitle2'
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '0.875rem',
										lineHeight: '157%',
										color: 'var(--primary-dark)',
									}}
								>
									Disponibilidade
								</Typography>
								<CustomButton
									color='secondary'
									size='small'
									tabIndex={-1}
									onClick={handleCalculateAvailability}
									isLoading={isFetchingAvailability}
								>
									Ver Disponibilidade
								</CustomButton>
							</Box>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										defaultChecked={TimesheetPage.dispRecalculateOnSave ?? true}
										onChange={handleRecalculateOnSave}
										tabIndex={-1}
										checked={TimesheetPage.dispRecalculateOnSave ?? true}
									/>
								}
								label='Recalcular automaticamente ao gravar a Folha'
								sx={{
									'& .MuiFormControlLabel-label': {
										fontFamily: 'var(--font-family)',
										fontSize: '0.8125rem',
										fontWeight: 500,
										lineHeight: '130%',
										color: 'var(--primary-dark)',
									},
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<CustomAlertDialog
				open={isInvalidDatesDialogOpen}
				title='Datas de serviço inválidas'
				description='As datas de início e fim dos serviços não estão válidas. Corrija os serviços antes de voltar a verificar a disponibilidade.'
				severity='warning'
				showCloseButton={false}
				closeOnBackdropClick={false}
				onRequestClose={() => setIsInvalidDatesDialogOpen(false)}
				actions={[
					{
						label: 'OK',
					},
				]}
			/>
		</>
	)
}

export default WorkRegimeAndAgent
